export enum Format {
  Advanced = 1,
  GOAT = 2,
  Edison = 3,
  HOTU = 4,
  Genesys = 5,
  Academy = 6,
  Mixed = 7,
  REDU = 8,
  HAT = 9,
  Tengu = 10,
  TOSS = 11,
  Worlds = 12,
  Custom = 13,
  // No es un formato de torneo: es el ranking unificado de los formatos
  // viejos. Existe como fila de `Format` porque los snapshots del ranking se
  // guardan por formatId. Ver TIME_WIZARD_FORMATS.
  TimeWizard = 14,
}

export enum GameType {
  TCG = 1,
  DuelLinks = 2,
  SpeedDuel = 3,
  DuelingBook = 4,
  MasterDuel = 5,
}

/**
 * Formatos que no se listan en las pantallas de gestión de torneos (la de
 * folder y la del organizador en tournament-app). No es una regla de negocio:
 * son torneos de escuela, muchos y de poco interés operativo, y ensucian el
 * listado. Se manda como `excludeFormatIds` para que el filtro sea del lado del
 * API y la paginación siga cuadrando.
 *
 * No lo apliques a las vistas personales (historial del jugador, mis torneos):
 * ahí el torneo de escuela es parte del registro de esa persona.
 */
export const UNLISTED_FORMATS: number[] = [Format.Academy];

export type GameFormat = {
  id: number;
  /** El nombre con el que se guarda y por el que se lo busca. */
  name: string;
  color: string;
  ranked: boolean;
  /** El nombre que se muestra. */
  label: string;
  /**
   * El otro nombre: el formal cuando la comunidad usa la sigla (HOTU es
   * "Heart of the Underdog") o al revés (al Mundial le dicen WCS).
   *
   * No es decorativo — `getFormatFromName` lo matchea igual que al `name`, así
   * que un link o un filtro escrito con el nombre de la comunidad resuelve.
   */
  alias?: string;
};

// Tupla no vacía, no `GameFormat[]`: la lista es una constante hardcodeada y
// media docena de call sites hacen `FORMATS[0]` como fallback. Tipada como
// array suelto, ese acceso es `GameFormat | undefined` y cada uno tenía que
// resolverlo por su cuenta.
export const FORMATS: [GameFormat, ...GameFormat[]] = [
  {
    id: Format.Advanced,
    name: 'Advanced',
    color: '#2563eb', // blue-600 - better contrast
    ranked: true,
    label: 'Advanced',
    alias: 'Advanced Constructed',
  },
  {
    id: Format.GOAT,
    name: 'GOAT',
    color: '#7c3aed', // violet-600 - better contrast
    ranked: false,
    label: 'GOAT',
  },
  {
    id: Format.Edison,
    name: 'Edison',
    color: '#ea580c', // orange-600 - better contrast
    ranked: false,
    label: 'Edison',
  },
  {
    id: Format.HOTU,
    name: 'HOTU',
    alias: 'Heart of the Underdog',
    color: '#dc2626', // red-600 - better contrast
    ranked: true,
    label: 'HOTU',
  },
  {
    id: Format.Genesys,
    name: 'Genesys',
    color: '#16a34a', // green-600 - better contrast
    ranked: true,
    label: 'Genesys',
  },
  {
    id: Format.Academy,
    name: 'Academy',
    color: '#0d9488', // teal-600 - better contrast
    ranked: false,
    label: 'Escuela',
  },
  {
    id: Format.Mixed,
    name: 'Mixed',
    color: '#c026d3', // fuchsia-600 - better contrast
    ranked: false,
    label: 'Mixto',
  },
  {
    id: Format.REDU,
    name: 'REDU',
    color: '#4f46e5', // indigo-600
    ranked: false,
    label: 'REDU',
  },
  {
    id: Format.HAT,
    name: 'HAT',
    color: '#d97706', // amber-600
    ranked: false,
    label: 'HAT',
  },
  {
    id: Format.Tengu,
    name: 'Tengu',
    color: '#65a30d', // lime-600
    ranked: false,
    label: 'Tengu',
  },
  {
    id: Format.TOSS,
    name: 'TOSS',
    color: '#e11d48', // rose-600
    ranked: false,
    label: 'TOSS',
  },
  {
    // El del mundial: el pool es la mezcla del TCG y el OCG, así que su lista
    // no es la de Konami de ninguno de los dos. Ver la task BuildWorldsBanlist.
    id: Format.Worlds,
    name: 'Mundial',
    alias: 'WCS',
    color: '#0891b2', // cyan-600
    ranked: false,
    label: 'Mundial',
  },
  {
    // Para los torneos raros que inventan las tiendas: no es un formato
    // concreto sino un cajón, y la lista la arma cada organizador como en HOTU.
    id: Format.Custom,
    name: 'Custom',
    alias: 'Personalizado',
    color: '#475569', // slate-600
    ranked: false,
    label: 'Custom',
  },
];

/**
 * Los formatos viejos que suman al ranking unificado de Time Wizard. Cada uno
 * sigue teniendo su ranking propio, que se ve dentro de la pestaña de Time
 * Wizard con el filtro por formato.
 *
 * El API lo usa para saber qué torneos juntar cuando le piden el ranking de
 * `Format.TimeWizard`.
 */
export const TIME_WIZARD_FORMATS: Format[] = [
  Format.GOAT,
  Format.Edison,
  Format.HAT,
  Format.Tengu,
];

export const isTimeWizardFormat = (formatId?: number | null) =>
  formatId != null && TIME_WIZARD_FORMATS.includes(formatId);

/**
 * Fuera de FORMATS a propósito: FORMATS alimenta los selectores de formato de
 * los torneos, y en Time Wizard no se juega.
 */
export const TIME_WIZARD_FORMAT: GameFormat = {
  id: Format.TimeWizard,
  name: 'Time Wizard',
  color: '#9333ea', // purple-600
  ranked: true,
  label: 'Time Wizard',
};

/** Las pestañas del ranking, en orden. */
export const RANKING_FORMATS: GameFormat[] = [
  ...FORMATS.filter((format) => format.ranked),
  TIME_WIZARD_FORMAT,
];

const ALL_FORMATS = [...FORMATS, TIME_WIZARD_FORMAT];

export const GAME_TYPES = [
  { id: GameType.TCG, name: 'TCG', color: '#2563eb' },
  { id: GameType.DuelLinks, name: 'Duel Links', color: '#06b6ff' },
  { id: GameType.SpeedDuel, name: 'Speed Duel', color: '#00fa99' },
  { id: GameType.DuelingBook, name: 'Dueling Book', color: '#da32f0' },
  { id: GameType.MasterDuel, name: 'Master Duel', color: '#5758a6' },
];

export const parseFormat = (formatId?: number) =>
  ALL_FORMATS.find((format) => format.id === formatId);

/**
 * El formato por cualquiera de sus dos nombres.
 *
 * Matchea también el `alias` porque es con lo que la gente lo escribe: buscar
 * "Heart of the Underdog" o "WCS" tiene que caer en el mismo formato que
 * "HOTU" o "Mundial".
 */
export const getFormatFromName = (name: string) => {
  // En una URL "Time Wizard" llega como "time-wizard".
  const normalize = (value: string) =>
    value.trim().toLowerCase().replace(/[-_]+/g, ' ');
  const needle = normalize(name);

  return ALL_FORMATS.find(
    (format) =>
      normalize(format.name) === needle ||
      (format.alias !== undefined && normalize(format.alias) === needle)
  );
};
